import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Phone, X, ExternalLink, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function VideoCallButton({ claim }) {
  const { user } = useAuth();
  const isManager = ["claim_manager", "admin"].includes(user?.role);
  const [room, setRoom] = useState(null);
  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/video/rooms/${claim.claim_id}`);
      setRoom(r.data);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 410) setRoom(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [claim.claim_id]);
  // Poll every 15s so the customer sees the "manager is on the line" state quickly
  useEffect(() => {
    if (isManager || room) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [isManager, room?.room_id]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api.post("/video/rooms", { claim_id: claim.claim_id });
      setRoom(r.data);
      toast.success("Call started — customer notified");
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start call");
    } finally {
      setStarting(false);
    }
  };

  const end = async () => {
    try {
      await api.post(`/video/rooms/${claim.claim_id}/end`);
      setRoom(null);
      setOpen(false);
      toast.success("Call ended");
    } catch (e) {
      toast.error("Failed to end");
    }
  };

  if (loading) return null;

  // Customer: only show a button when the manager has started a room
  if (!isManager) {
    if (!room) return null;
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className="rounded-2xl border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 p-4 flex items-center gap-3 w-full hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors group"
          data-testid="video-call-join"
        >
          <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 animate-pulse">
            <Video className="w-5 h-5" />
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="font-heading font-bold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Your claim manager is on the line
            </div>
            <div className="text-xs text-emerald-700 dark:text-emerald-300">Tap to join the video call now</div>
          </div>
          <div className="text-emerald-700 group-hover:translate-x-1 transition-transform">→</div>
        </button>
        {open && <CallModal room={room} onClose={() => setOpen(false)} />}
      </>
    );
  }

  // Claim manager: show start/join controls
  return (
    <>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-center gap-3" data-testid="video-call-manager">
        <div className="w-10 h-10 rounded-full bg-[#003DA5] text-white flex items-center justify-center shrink-0">
          <Video className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-slate-900 dark:text-white">Video call the customer</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">One tap · sends a WhatsApp link · secured room</div>
        </div>
        {room ? (
          <div className="flex gap-2">
            <button onClick={() => setOpen(true)} className="btn-signature rounded-lg px-3 py-2 text-sm font-semibold" data-testid="video-call-open">
              Open room
            </button>
            <button onClick={end} className="rounded-lg px-3 py-2 text-sm font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-500/30 dark:hover:bg-rose-500/10" data-testid="video-call-end">
              End
            </button>
          </div>
        ) : (
          <button
            onClick={start}
            disabled={starting}
            className="btn-signature rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-60"
            data-testid="video-call-start"
          >
            {starting ? "Starting…" : (<><Phone className="w-3.5 h-3.5 inline mr-1" /> Start call</>)}
          </button>
        )}
      </div>
      {open && room && <CallModal room={room} onClose={() => setOpen(false)} />}
    </>
  );
}

function CallModal({ room, onClose }) {
  const { user } = useAuth();
  const isManager = ["claim_manager", "admin"].includes(user?.role);
  const joinUrl = isManager ? room.manager_url : room.customer_url;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-950 rounded-2xl w-full max-w-4xl h-[85vh] overflow-hidden shadow-2xl relative border border-slate-800"
          data-testid="video-call-modal"
        >
          <div className="absolute top-3 right-3 z-10 flex gap-2">
            <a
              href={joinUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-white/10 hover:bg-white/20 text-white rounded-full px-3 py-1.5 text-xs font-semibold backdrop-blur inline-flex items-center gap-1"
              data-testid="video-call-external"
            >
              <ExternalLink className="w-3 h-3" /> Open in new tab
            </a>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
              aria-label="Close"
              data-testid="video-call-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <iframe
            src={`${joinUrl}#config.prejoinConfig.enabled=false&config.disableDeepLinking=true`}
            title="Video call"
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            className="w-full h-full"
            data-testid="video-call-iframe"
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
